/**
 * Générateurs de classes CSS, partagés par les adaptateurs React et Vue, et
 * utilisables tels quels (Angular, Svelte, templates serveur…).
 *   buttonClass({ variant: 'outline', size: 'sm' }) // "hn-btn hn-btn--outline hn-btn--sm"
 */

export type ClassValue = string | number | false | null | undefined | ClassValue[] | Record<string, unknown>;

/** Concatène des classes en ignorant les valeurs vides (comme clsx). */
export function cx(...values: ClassValue[]): string {
  const out: string[] = [];
  const walk = (value: ClassValue) => {
    if (!value) return;
    if (typeof value === 'string' || typeof value === 'number') out.push(String(value));
    else if (Array.isArray(value)) value.forEach(walk);
    else for (const [key, on] of Object.entries(value)) if (on) out.push(key);
  };
  values.forEach(walk);
  return out.join(' ');
}

export type ButtonVariant = 'primary' | 'secondary' | 'soft' | 'outline' | 'ghost' | 'link' | 'danger' | 'success';
export type ControlSize = 'xs' | 'sm' | 'md' | 'lg';
export type StatusVariant = 'info' | 'success' | 'warning' | 'danger';

export interface ButtonClassOptions {
  variant?: ButtonVariant;
  size?: ControlSize;
  shape?: 'default' | 'pill' | 'petal';
  block?: boolean;
  iconOnly?: boolean;
}

export function buttonClass({ variant = 'primary', size = 'md', shape = 'default', block, iconOnly }: ButtonClassOptions = {}): string {
  return cx('hn-btn', variant !== 'primary' && `hn-btn--${variant}`, size !== 'md' && `hn-btn--${size}`, shape !== 'default' && `hn-btn--${shape}`, {
    'hn-btn--block': block,
    'hn-btn--icon': iconOnly,
  });
}

export type BadgeVariant = 'sakura' | 'solid' | 'neutral' | 'accent' | 'outline' | StatusVariant;

export function badgeClass({ variant = 'sakura', dot, size = 'md' }: { variant?: BadgeVariant; dot?: boolean; size?: 'md' | 'lg' } = {}): string {
  return cx('hn-badge', variant !== 'sakura' && `hn-badge--${variant}`, { 'hn-badge--dot': dot, 'hn-badge--lg': size === 'lg' });
}

export function tagClass({ selected }: { selected?: boolean } = {}): string {
  return cx('hn-tag', { 'hn-tag--selected': selected });
}

export type AlertVariant = StatusVariant | 'sakura';

export function alertClass({ variant = 'info', banner, plain }: { variant?: AlertVariant; banner?: boolean; plain?: boolean } = {}): string {
  return cx('hn-alert', variant !== 'info' && `hn-alert--${variant}`, { 'hn-alert--banner': banner, 'hn-alert--plain': plain });
}

export type CardVariant = 'default' | 'elevated' | 'flat' | 'outline' | 'sakura';

export function cardClass({ variant = 'default', interactive }: { variant?: CardVariant; interactive?: boolean } = {}): string {
  return cx('hn-card', variant !== 'default' && `hn-card--${variant}`, { 'hn-card--interactive': interactive });
}

export function inputClass({ size = 'md', pill }: { size?: 'sm' | 'md' | 'lg'; pill?: boolean } = {}): string {
  return cx('hn-input', size !== 'md' && `hn-input--${size}`, { 'hn-input--pill': pill });
}

export function selectClass({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' } = {}): string {
  return cx('hn-select', size !== 'md' && `hn-input--${size}`);
}

export function avatarClass({ size = 'md', square }: { size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'; square?: boolean } = {}): string {
  return cx('hn-avatar', size !== 'md' && `hn-avatar--${size}`, { 'hn-avatar--square': square });
}

export function spinnerClass({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' | 'xl' } = {}): string {
  return cx('hn-spinner', size !== 'md' && `hn-spinner--${size}`);
}

export function progressClass({ size = 'md', variant }: { size?: 'sm' | 'md' | 'lg'; variant?: Exclude<StatusVariant, 'info'> } = {}): string {
  return cx('hn-progress', size !== 'md' && `hn-progress--${size}`, variant && `hn-progress--${variant}`);
}

/** Initiales d'un nom, pour les avatars : « Sakura Tanaka » → « ST ». */
export function initials(name = ''): string {
  return name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]!.toUpperCase())
    .join('');
}
